import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import { SiteResponse } from "../types";
import { queryKeys } from "./queryKeys";

const onGetSite = async (siteId: string) => {
  const { data } = await axios.get(`/site/${siteId}`);
  return data;
};

export const useSite = (siteId: string) =>
  useQuery({
    queryKey: [queryKeys.SITE, siteId],
    queryFn: () => onGetSite(siteId),
    enabled: !!siteId,
  });

const onCreateSite = async (body: any) => {
  const { data } = await axios.post("/site", body);
  return data;
};

// `onError` takes the failed mutation's error (previously `() => void`, discarding it): Task 6
// needed callers to distinguish a duplicate-`internalCode` failure from any other, which means
// reading the response the mutation already received rather than only knowing THAT it failed.
// Every existing caller keeps working unchanged — a zero-arg callback is still assignable to a
// one-arg callback type, same as any DOM event handler that ignores its event.
//
// Invalidation added in the final fix wave (F2): this used to invalidate nothing, relying
// entirely on NewSiteForm's own navigate-to-/sites-on-success plus TanStack's `staleTime: 0`
// default to mask it — the exact shape `useUpdateUser`'s own comment documents finding broken
// for Users, and something even the unmigrated `InlineCreateSiteModal.tsx` (which explicitly
// calls `queryClient.invalidateQueries({ queryKey: [queryKeys.SITES] })` itself after a create)
// already did better at its own call site. Same keys `useSetSiteActive` below invalidates, for
// the same prefix-match reason: `[queryKeys.SITES]` matches `useSites`' real key
// (`[queryKeys.SITES, companyId]`) by TanStack's default partial-match, so every company-scoped
// roster this hook has ever been called with gets refetched; `[queryKeys.SITE]` covers
// `useSite(id)` the same way, for a caller who creates a site then immediately opens it.
// `[queryKeys.SITES_PAGINATED]` (Task 8b) covers `usePaginatedSitesByCompany`'s key
// (`[queryKeys.SITES_PAGINATED, page, size, query]`) by the same prefix match — Sites.tsx now
// reads exclusively from that query, so a create that didn't invalidate it would show a stale
// page/count until something else happened to refetch it.
export const useCreateSite = (
  onSuccess: () => void,
  onError: (error: unknown) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: any) => onCreateSite(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.SITES] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.SITE] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.SITES_PAGINATED] });
      onSuccess();
    },
    onError,
  });
};

const onEditSite = async ({ siteId, body }: { siteId: string; body: any }) => {
  const { data } = await axios.put(`/site/${siteId}`, body);
  return data;
};

// Same widening as `useCreateSite` above, and for the same reason — `EditSiteForm` needs the
// error to tell a duplicate `internalCode` apart from anything else `PUT /site/{id}` can fail
// with.
//
// Same F2 invalidation fix as `useCreateSite` above, and for the same reason: `[queryKeys.SITES]`
// so the list (address/comment/name all render there) doesn't show stale values, `[queryKeys.SITE]`
// so re-opening the same site's edit form doesn't show them either, and (Task 8b)
// `[queryKeys.SITES_PAGINATED]` so Sites.tsx's own paginated roster — the screen an edit actually
// navigates back to — doesn't either.
export const useUpdateSite = (
  onSuccess: () => void,
  onError: (error: unknown) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: onEditSite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.SITES] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.SITE] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.SITES_PAGINATED] });
      onSuccess();
    },
    onError,
  });
};

const onGetSites = async (companyId: string): Promise<SiteResponse[]> => {
  const { data } = await axios.get(`/site/company/${companyId}`);
  return data;
};

// GET /site/company/{id} (findSitesByCompany) filters only `deleted` — inactive sites are
// already on the wire today. Task 8a added a paginated/searchable sibling endpoint
// (`usePaginatedSitesByCompany` below) alongside this one, not as a replacement: seven picker
// consumers across unmigrated flows (NewDeliveryForm, NewMaterialForm, Order, Materials,
// OrderByRequest, PurchaseRequestsV2, EditMaterial) still need this hook's full, unpaginated
// company roster and are deliberately untouched by Task 8b — only Sites.tsx's own list screen
// moved to the paginated query.
export const useSites = (companyId: string) =>
  useQuery({
    queryKey: [queryKeys.SITES, companyId],
    queryFn: () => onGetSites(companyId),
    enabled: !!companyId,
  });

/**
 * The lean row shape `GET /site/company/paginated` actually returns (`SiteListResponse` on the
 * backend) — deliberately NOT `SiteResponse`: no `company`, `country`, `createdAt`,
 * `googlePlaceId`, `updatedAt`. Sites.tsx's row mapping only ever read `id`/`internalCode`/
 * `name`/`address`/`comment`/`active` off a site to begin with, so this type is a floor that was
 * already true of every field this screen uses, not a narrowing that drops something needed.
 */
export interface SiteListItem {
  id: string;
  internalCode: string;
  name: string;
  address: string;
  comment: string;
  active: boolean;
}

export interface PaginatedSitesResponse {
  content: SiteListItem[];
  totalElements: number;
  totalPages: number;
  number: number;
}

const onGetPaginatedSitesByCompany = async (
  page: number,
  size: number,
  query: string
): Promise<PaginatedSitesResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  if (query) params.append("query", query);
  const { data } = await axios.get(
    `/site/company/paginated?${params.toString()}`
  );
  return data;
};

// Mirrors `usePaginatedUsersByCompany` (hooks/useUsers.ts) field-for-field, including no company
// id parameter: the backend resolves the caller's company from the token, same as the paginated
// Users endpoint — there is no ownership check to get wrong here, by construction, unlike
// `useSites` above (a path-parameter `companyId` the backend must verify against the caller).
export const usePaginatedSitesByCompany = (
  page: number,
  size: number,
  query: string
) =>
  useQuery<PaginatedSitesResponse>({
    queryKey: [queryKeys.SITES_PAGINATED, page, size, query],
    queryFn: () => onGetPaginatedSitesByCompany(page, size, query),
    placeholderData: (previous) => previous,
  });

const onSetSiteActive = async ({
  siteId,
  active,
}: {
  siteId: string;
  active: boolean;
}) => {
  const { data } = await axios.put(`/site/${siteId}/active`, { active });
  return data;
};

// Mirrors useSetUserActive (hooks/useUsers.ts). `useSites`' own query key is
// `[queryKeys.SITES, companyId]` — invalidating `[queryKeys.SITES]` alone matches it by PREFIX
// (TanStack Query's default partial-match behaviour), so every company-scoped roster this hook
// has ever been called with gets refetched, not just whichever one happened to be mounted last.
// This is the fixed shape useUpdateUser's own comment documents finding broken (a compound key
// that was never actually a prefix of anything) — copied correctly here rather than rediscovered.
// `[queryKeys.SITES_PAGINATED]` (Task 8b) covers `usePaginatedSitesByCompany` the same way —
// this is the mutation Sites.tsx's own toggle calls directly, so it's the one whose staleness
// would be most immediately visible (the CURRENT page showing the pre-toggle status) if this key
// were missing.
export const useSetSiteActive = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: onSetSiteActive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.SITES] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.SITE] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.SITES_PAGINATED] });
      onSuccess();
    },
    onError,
  });
};
